import React, { lazy, Suspense } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import Loading from '../common/components/Loading';
import { isFirstVisit } from '../utils/firstVisit';
import { buildCategoryUrl } from '../utils/categoryUrl';

const Main = lazy(() => import('../app/main/ui/Main'));
const Category = lazy(() => import('../app/category/ui/Main'));
const Announcement = lazy(() => import('../app/announcement/ui/Main'));
const Sign = lazy(() => import('../app/sign/ui/Main'));
const SmsCode = lazy(() => import('../app/sign/sms/SmsCode'));
const Register = lazy(() => import('../app/sign/in/Register'));
const RegisterWelcome = lazy(() => import('../app/sign/welcome/Welcome'));
const QrLoginLanding = lazy(() => import('../app/sign/qr/QrLoginLanding'));
const PayLanding = lazy(() => import('../app/order/ui/PayLanding'));
const Profile = lazy(() => import('../app/profile/ui/Main'));
const AdForm = lazy(() => import('../app/adform/ui/Main'));
const AboutUs = lazy(() => import('../app/aboutus/ui/Main'));
const PolicyPage = lazy(() => import('../app/aboutus/pages/policy/Main'));
const TermsPage = lazy(() => import('../app/aboutus/pages/terms/Main'));
const BusinessOfferPage = lazy(() => import('../app/aboutus/pages/businessOffer/Main'));
const Review = lazy(() => import('../app/review/ui/Review'));
const Promote = lazy(() => import('../app/promote/ui/Main'));
const Company = lazy(() => import('../app/company/ui/Main'));
const Edit = lazy(() => import('../app/edit/ui/Main'));
const Reviews = lazy(() => import('../app/reviews/ui/Main'));
const News = lazy(() => import('../app/news/ui/Main'));
const Adshow = lazy(() => import('../app/adshow/ui/Main'));
const Notifications = lazy(() => import('../app/notifications/ui/Main'));

const OrderCreate = lazy(() => import('../app/order/ui/Main'));
const TeamPoolPage = lazy(() => import('../app/teamPool/ui/TeamPool'));
const ServicesPage = lazy(() => import('../app/services/ui/Main'));
const EgovPage = lazy(() => import('../app/services/components/EgovPage'));
const VatSettings = lazy(() => import('../app/admin/ui/VatSettings'));
const KaspiQr = lazy(() => import('../app/kaspiQr/ui/KaspiQr'));
const Paloma = lazy(() => import('../app/paloma/ui/Paloma'));
const NotFound = lazy(() => import('../app/notfound/ui/NotFound'));

// ChinaMainPage / ChinaCategoryPage were folded into the Mercuryx tab on
// the home page — only the product detail and order history live at own URLs.
const ChinaProductPage = lazy(() => import('../app/china/ui/ChinaProductPage'));
const ChinaOrdersPage = lazy(() => import('../app/china/ui/ChinaOrdersPage'));

const withSuspense = (Component) => (
    <Suspense fallback={<Loading />}>
        <Component />
    </Suspense>
);

const RedirectWithQuery = ({ to }) => {
    const { search, hash } = useLocation();
    return <Navigate replace to={`${to}${search}${hash}`} />;
};

// Ескі /category/:name/:sub/:id/:subId сілтемелерін жаңа slug-форматқа бұрамыз.
const LegacyCategoryRedirect = () => {
    const { category_name, subcategory_name } = useParams();
    const { search, hash } = useLocation();
    const clean = (name) => (name === 'all' || name === 'null' || name === '0' ? '' : name);
    const to = buildCategoryUrl({ name: clean(category_name) }, { name: clean(subcategory_name) });
    return <Navigate replace to={`${to}${search}${hash}`} />;
};

// Сайтқа алғаш кірген қонақты қарсы алу бетінен (/welcome) бастаймыз;
// кейін ол еркін шарлай алады — «көрдім» жалауын /welcome өзі қояды.
// Кіріп қойған қолданушы бірден басты бетті көреді.
const FirstVisitGate = () => {
    if (isFirstVisit()) return <Navigate replace to="/welcome" />;
    return withSuspense(Main);
};

const routes = [
    { path: '/', element: <FirstVisitGate /> },
    { path: '/category', element: withSuspense(Category) },
    { path: '/category/:categorySlug', element: withSuspense(Category) },
    { path: '/category/:categorySlug/:subcategorySlug', element: withSuspense(Category) },
    { path: '/category/:category_name/:subcategory_name/:category_id/:subcategory_id', element: <LegacyCategoryRedirect /> },
    { path: '/announcement/:slugOrId', element: withSuspense(Announcement) },
    { path: '/sign', element: withSuspense(Sign) },
    { path: '/sms-code', element: withSuspense(SmsCode) },
    { path: '/register', element: withSuspense(Register) },
    { path: '/welcome', element: withSuspense(RegisterWelcome) },
    { path: '/qr-login', element: withSuspense(QrLoginLanding) },
    { path: '/pay', element: withSuspense(PayLanding) },
    { path: '/pay/result', element: withSuspense(PayLanding) },
    { path: '/pay/card-result', element: withSuspense(PayLanding) },
    { path: '/profile', element: withSuspense(Profile) },
    { path: '/adform', element: withSuspense(AdForm) },
    { path: '/preview', element: withSuspense(Review) },
    { path: '/aboutus', element: withSuspense(AboutUs) },
    { path: '/privacy-policy', element: withSuspense(PolicyPage) },
    { path: '/terms-of-use', element: withSuspense(TermsPage) },
    // Оферта 15.5: қолданыстағы редакция дәл осы мекенжайда жарияланады.
    { path: '/business-offer', element: withSuspense(BusinessOfferPage) },
    { path: '/oferta', element: <RedirectWithQuery to="/business-offer" /> },
    { path: '/requisites', element: <Navigate replace to="/aboutus#requisites" /> },
    { path: '/payment', element: <Navigate replace to="/aboutus#payment" /> },
    { path: '/kaspi-QR', element: withSuspense(KaspiQr) },
    { path: '/paloma', element: withSuspense(Paloma) },
    { path: '/policy', element: <RedirectWithQuery to="/privacy-policy" /> },
    { path: '/terms', element: <RedirectWithQuery to="/terms-of-use" /> },
    { path: '/promote', element: withSuspense(Promote) },
    { path: '/company', element: withSuspense(Company) },
    { path: '/company/:id', element: withSuspense(Company) },
    { path: '/edit', element: withSuspense(Edit) },
    { path: '/reviews/:id', element: withSuspense(Reviews) },
    { path: '/news/:service/:id', element: withSuspense(News) },
    { path: '/adshow/:id', element: withSuspense(Adshow) },
    { path: '/notifications', element: withSuspense(Notifications) },
    { path: '/order/new', element: withSuspense(OrderCreate) },
    { path: '/order/:id', element: withSuspense(OrderCreate) },
    { path: '/team-pool', element: withSuspense(TeamPoolPage) },
    { path: '/services', element: withSuspense(ServicesPage) },
    { path: '/services/egov', element: withSuspense(EgovPage) },
    { path: '/profile/services', element: withSuspense(ServicesPage) },
    { path: '/profile/services/egov', element: withSuspense(EgovPage) },
    { path: '/admin/vat-settings', element: withSuspense(VatSettings) },
    // /china main + category are the same content that already lives inside
    // the Mercuryx tab on the home page — keep the URLs as bookmark-friendly
    // redirects, but drop the standalone rendering.
    { path: '/china', element: <Navigate replace to="/?mode=china" /> },
    { path: '/china/category/:slug', element: <Navigate replace to="/" /> },
    { path: '/china/product/:id', element: withSuspense(ChinaProductPage) },
    { path: '/china/orders', element: withSuspense(ChinaOrdersPage) },
    { path: '/china/cart', element: <Navigate replace to="/" /> },
    { path: '*', element: withSuspense(NotFound) },
];

export default routes;